import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import Spacer from '@components/Spacer';
import { Input } from '@components/DesignLibrary/Atoms';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import { CustomTermsDynamicVariableType } from '@typings/api-models';
import s from './AdditionalTermsInfo.module.scss';
import AssignUserToSignature from './AssignUserToSignature';
import ReviewPage from './reviewPage';
import Button from '../DesignLibrary/Button/Button';

type PersonBasic = Pick<ApiModels.Person, 'id' | 'name' | 'email'> & {
  color?: string | null;
  logo?: string | null;
  source?: string | null;
};

type Props = {
  initialValues?: Record<string, string>;
  additionalFields: { label: string, key: string, value?: string | null }[];
  onSubmit: (data: Record<string, string | number | null | undefined>, variablePersonId?: {
    variableName: string;
    personId: string;
  }[]) => void;
  onFinish?: () => void;
  userWorkflowId?: string | null;
  signatureData?: { key: string; type: CustomTermsDynamicVariableType; }[];
};

type Step = {
  type: 'InputPage' | 'SignatureSelect' | 'ReviewPage';
  variable?: { key: string; type: CustomTermsDynamicVariableType; };
};

const CartAdditionalTermsInfo: FC<Props> = ({
  additionalFields,
  onFinish,
  onSubmit,
  userWorkflowId,
  signatureData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fieldsData, setFieldsData] = useState<Record<string, string | number | null | undefined>>(() => {
    const initialData: Record<string, string | number | null | undefined> = {};
    additionalFields.forEach(field => {
      initialData[field.key] = field.value || null;
    });
    return initialData;
  });
  const [assignedPeople, setAssignedPeople] = useState<{ variableName: string; personId: string; }[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const {
    data: loadedCollaborators,
    getData: getCollaboratorsFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators, { toastOnError: true });

  const steps = useMemo(() => {
    const stepsArray: Step[] = [{ type: 'InputPage' }];

    if ((signatureData ?? []).length > 1) {
      signatureData?.forEach(variable => {
        stepsArray.push({
          type: 'SignatureSelect',
          variable,
        });
      });
      stepsArray.push({ type: 'ReviewPage' });
    }

    return stepsArray;
  }, [signatureData]);

  const step = steps[currentStep];

  // Get the current signature step index (0-based)
  const currentSignatureStepIndex = useMemo(() => {
    if (!step || step.type !== 'SignatureSelect') return -1;
    return steps.findIndex(st => st.type === 'SignatureSelect' && st.variable?.key === step.variable?.key);
  }, [step, steps]);

  // Get the current variable key
  const currentVariableKey = useMemo(() => {
    if (!step || step.type !== 'SignatureSelect' || !step.variable) {
      return null;
    }
    return step.variable.key;
  }, [step]);

  useEffect(() => {
    if (userWorkflowId) {
      getCollaboratorsFn({ userWorkflowId });
    }
  }, [getCollaboratorsFn, userWorkflowId]);

  useEffect(() => {
    mixpanelTrack('Visited CartAdditionalTermsInfo');
  }, []);

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    await onSubmit(fieldsData, assignedPeople);
    onFinish?.();
    setIsLoading(false);
  };

  const handleInputFieldsData = (): void => {
    if ((signatureData ?? []).length < 2) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleAssignPersonToVariable = (user: PersonBasic): void => {
    if (step.type === 'SignatureSelect' && step.variable) {
      const newAssignedPeople = [...assignedPeople];
      const existingIndex = newAssignedPeople.findIndex(
        p => p.variableName === step.variable!.key,
      );

      if (existingIndex !== -1) {
        // Update existing assignment
        newAssignedPeople[existingIndex] = {
          personId: user.id,
          variableName: step.variable.key,
        };
      } else {
        // Add new assignment
        newAssignedPeople.push({
          personId: user.id,
          variableName: step.variable.key,
        });
      }
      setAssignedPeople(newAssignedPeople);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = (): void => {
    if (step.type === 'SignatureSelect' && currentVariableKey) {
      // Remove the selected user for the current step
      setAssignedPeople(prev =>
        prev.filter(p => p.variableName !== currentVariableKey),
      );
    }
    setCurrentStep(prev => prev - 1);
  };

  const handleFieldChange = (key: string, val: string): void => {
    setFieldsData({
      ...fieldsData,
      [key]: val,
    });
  };

  const isDisabled = (): boolean => {
    for (let i = 0; i < additionalFields.length; i += 1) {
      if (!fieldsData[additionalFields[i].key] && !additionalFields[i]?.value) {
        return true;
      }
    }
    return false;
  };

  // Get the currently selected user for the current step
  const getCurrentSelectedUser = useMemo(() => {
    if (step?.type !== 'SignatureSelect' || !currentVariableKey) return null;

    const currentAssignment = assignedPeople.find(
      assignment => assignment.variableName === currentVariableKey,
    );

    return loadedCollaborators?.find(c => c.id === currentAssignment?.personId) || null;
  }, [step, currentVariableKey, assignedPeople, loadedCollaborators]);

  if (!step) {
    return null;
  }

  return (
    <div className={s.container}>
      {step.type === 'InputPage' && (
        <div>
          <p className='body-sm-bold'>Before generating to the terms, please confirm the information below.</p>
          <Spacer size={20} />
          {additionalFields.map((field) => (
            <Fragment key={field.key}>
              <Input
                id={field.key}
                label={field.label}
                name={field.key}
                placeholder={`Enter ${field.label}`}
                onChange={(value) => handleFieldChange(field.key, value)}
                value={fieldsData[field.key] as string || field.value}
              />
              <Spacer size={16}/>
            </Fragment>
          ))}
          <Button
            disabled={isDisabled() && !isLoading}
            variant="primary"
            size="md"
            fullWidth
            onClick={handleInputFieldsData}
          >
            Next
          </Button>
        </div>
      )}

      {step.type === 'SignatureSelect' && step.variable && (
        <AssignUserToSignature
          onUserSelect={handleAssignPersonToVariable}
          selectedUser={getCurrentSelectedUser}
          collaborators={loadedCollaborators || []}
          onBack={handleBack}
          label={`Who is your ${step.variable.key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}?`}
          excludedUserIds={currentSignatureStepIndex === 0
            ? []
            : assignedPeople
              .filter(p => p.variableName !== currentVariableKey)
              .map(p => p.personId)}
        />
      )}

      {step.type === 'ReviewPage' && (
        <ReviewPage
          primaryUser={loadedCollaborators?.find(c => c.id === assignedPeople[0]?.personId) || null}
          secondaryUser={loadedCollaborators?.find(c => c.id === assignedPeople[1]?.personId) || null}
          onConfirm={handleSubmit}
          onBack={handleBack}
          additionalFields={additionalFields}
          fieldsData={fieldsData}
          signatureData={signatureData}
        />
      )}
    </div>
  );
};

export default CartAdditionalTermsInfo;
